import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { sitesListAtom, sitesLoadingAtom, sitesErrorAtom } from '../../state/sitesAtom';
import { userAtom, authLoadingAtom } from '../../state/userAtom';
import { licenseAtom, licenseLoadingAtom, licenseErrorAtom } from '../../state/licenseAtom';
import { sitesApi, authApi, licenseApi } from '../../api';

function AppInitializer({ children }) {
  const setSites = useSetRecoilState(sitesListAtom);
  const setSitesLoading = useSetRecoilState(sitesLoadingAtom);
  const setSitesError = useSetRecoilState(sitesErrorAtom);
  const setUser = useSetRecoilState(userAtom);
  const setAuthLoading = useSetRecoilState(authLoadingAtom);
  const setLicense = useSetRecoilState(licenseAtom);
  const setLicenseLoading = useSetRecoilState(licenseLoadingAtom);
  const setLicenseError = useSetRecoilState(licenseErrorAtom);

  useEffect(() => {
    // Fetch current user if token exists
    const initAuth = async () => {
      if (authApi.isAuthenticated()) {
        try {
          const response = await authApi.getMe();
          setUser(response.user);
          return true; // Auth successful
        } catch (err) {
          // Token invalid, clear it
          authApi.logout();
        }
      }
      setAuthLoading(false);
      return false;
    };

    // Fetch sites (requires auth)
    const initSites = async () => {
      setSitesLoading(true);
      try {
        const response = await sitesApi.getAll();
        setSites(response.data || []);
      } catch (err) {
        setSitesError(err.message);
      } finally {
        setSitesLoading(false);
      }
    };

    // Fetch license status (public endpoint)
    const initLicense = async () => {
      setLicenseLoading(true);
      try {
        const response = await licenseApi.getStatus();
        setLicense(response.data || { isValid: false, status: 'none' });
      } catch (err) {
        setLicenseError(err.message);
        setLicense({ isValid: false, status: 'error' });
      } finally {
        setLicenseLoading(false);
      }
    };

    // Initialize in sequence: auth first, then sites (which requires auth)
    const init = async () => {
      const isAuthenticated = await initAuth();
      setAuthLoading(false);

      // Only fetch sites if authenticated
      if (isAuthenticated) {
        await initSites();
      } else {
        setSitesLoading(false);
      }
    };

    init();
    // License status is public - can fetch in parallel
    initLicense();

    // Listen for unauthorized events
    const handleUnauthorized = () => {
      setUser(null);
    };

    window.addEventListener('wr:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('wr:unauthorized', handleUnauthorized);
    };
  }, [setSites, setSitesLoading, setSitesError, setUser, setAuthLoading, setLicense, setLicenseLoading, setLicenseError]);

  return children;
}

export default AppInitializer;
